import * as React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import "../Amenities.css"


import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';

import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import apiClient, { shouldRedirectToLogin, getErrorMessage } from '../services/api-client';
import AppConfig from '../services/app-config';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AuthService from '../services/auth.js';

import appConfig from '../services/app-config';
import { useSwipeable } from 'react-swipeable';
import { motion, AnimatePresence } from 'framer-motion';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';

import PageHeader from '../components/PageHeader';

const statusChipColors = {
  'reportado': { bg: '#fff3e0', color: '#e65100', label: 'Pendiente' },
  'en_progreso': { bg: '#e3f2fd', color: '#1565c0', label: 'En Proceso' },
  'asignado': { bg: '#e8f5e9', color: '#2e7d32', label: 'Asignado' },
  'programado': { bg: '#ede7f6', color: '#4527a0', label: 'Programado' },
  'en_espera': { bg: '#f3e5f5', color: '#7b1fa2', label: 'En espera' },
  'resuelto': { bg: '#e8f5e9', color: '#2e7d32', label: 'Resuelto' },
  'cancelado': { bg: '#fbe9e7', color: '#bf360c', label: 'Cancelado' },
};

function getStatusChip(estadoKey, stateFriendlyNames) {
  const preset = statusChipColors[estadoKey];
  if (preset) return preset;
  const friendly = stateFriendlyNames && stateFriendlyNames[estadoKey];
  return { bg: '#f5f5f5', color: '#616161', label: friendly ? friendly.nombre_corto : estadoKey };
}

function IssueListItem({ issue, apiUrl, stateFriendlyNames, onClick, showPendingDot }) {
  const chipInfo = getStatusChip(issue.estado.estado, stateFriendlyNames);
  return (
    <Card
      sx={{
        mb: 1, borderRadius: '10px', cursor: 'pointer',
        border: '1px solid', borderColor: 'divider', boxShadow: 0,
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
        transition: 'box-shadow 0.2s'
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box
            component="img"
            src={`${apiUrl}/img/issue/${issue.id}.jpg`}
            alt={issue.titulo}
            sx={{
              width: 56, height: 56, borderRadius: 2, objectFit: 'cover',
              flexShrink: 0, backgroundColor: '#f0f0f0'
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
              <Chip
                label={chipInfo.label}
                size="small"
                sx={{
                  height: 20, fontSize: '0.65rem', fontWeight: 700,
                  backgroundColor: chipInfo.bg, color: chipInfo.color,
                  borderRadius: 1, '& .MuiChip-label': { px: 1 }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {appConfig.formatFriendlyDateWithYear(issue.estado.fecha)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3, minWidth: 0, flex: 1 }} noWrap>
                {issue.titulo}
              </Typography>
              {showPendingDot && (
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#d32f2f',
                    boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.45)',
                    animation: 'pendingDotPulse 1.8s ease-in-out infinite',
                    flexShrink: 0,
                    '@keyframes pendingDotPulse': {
                      '0%': { opacity: 0.45, transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.40)' },
                      '50%': { opacity: 1, transform: 'scale(1.05)', boxShadow: '0 0 0 4px rgba(211, 47, 47, 0.12)' },
                      '100%': { opacity: 0.45, transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
                    }
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap>
              {issue.area ? AppConfig.titleCase(issue.area) : ''}
              {issue.area && issue.creador ? ' • ' : ''}
              {issue.creador ? AppConfig.titleCase(issue.creador.usuario_nombre) : ''}
            </Typography>
            {stateFriendlyNames && stateFriendlyNames[issue.estado.estado] && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                <AccessTimeIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                  {stateFriendlyNames[issue.estado.estado].nombre_accion} {appConfig.formatFriendlyDateWithYear(issue.estado.fecha)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}


export default function IssueReport({setNavbarState, navbarState}) {
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [activeIssues, setActiveIssues] = React.useState([]);
  const [resolvedIssues, setResolvedIssues] = React.useState([]);
  const [showBackdrop, setShowBackdrop] = React.useState(false);
  const [tabValue, setTabValue] = React.useState('active');
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
  const apiUrl = process.env.REACT_APP_MGO_API_URL;
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [total, setTotal] = React.useState(0);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const PAGE_SIZE = 20;
  const sentinelRef = React.useRef(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [showSearch, setShowSearch] = React.useState(false);
  const searchInputRef = React.useRef(null);
  const [debouncedSearch, setDebouncedSearch] = React.useState('');

  let snackbarmsg = new URLSearchParams(location.search).get('msg');
  let snackbarlvl = new URLSearchParams(location.search).get('lvl',"success");
  let tab = new URLSearchParams(location.search).get('tab');

  
  const pendingCount = activeIssues.filter((issue) => issue.estado?.estado === 'reportado').length;

  const handleTabChange = (event,newValue) => {
    setTabValue(newValue);
    setOffset(0);
    setHasMore(false);
    if (newValue === 'pending') {
      if (activeIssues.length === 0) {
        fetchInfo('active');
      }
      return;
    }
    fetchInfo(newValue);
  }

  const handleSearchChange = (event) => setSearchValue(event.target.value);
  const handleToggleSearch = () => {
    setShowSearch((prev) => {
      const next = !prev;
      if (!next) setSearchValue('');
      return next;
    });
  };
  React.useEffect(() => {
    if (showSearch) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [showSearch]);
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const stateFriendlyNames = AppConfig.getLocal("issues")

  const searchRef = React.useRef('');
  React.useEffect(() => {
    searchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  const fetchInfo = (CurrentTabValue) => {
    setShowBackdrop(true);
    setLoading(true);
    setOffset(PAGE_SIZE);
    const qParam = searchRef.current ? `&q=${encodeURIComponent(searchRef.current)}` : '';
    apiClient.get(`issues/${CurrentTabValue}/list?limit=${PAGE_SIZE}&offset=0${qParam}`, {}, { action: 'fetch_issues_list' })
      .then(response => {
        if (response.status === 200) {
          const data = response.data.data || {};
          const items = data.items || [];
          if (CurrentTabValue === 'active') {
            setActiveIssues(items);
            if (snackbarmsg !== null) {
              setSnackbar({ open: true, message: snackbarmsg, severity: snackbarlvl });
              setRedirect({ trigger: true, route: '/amenidades' });
              snackbarmsg = null;
            }
          }
          else if (CurrentTabValue === 'resolved') {
            setResolvedIssues(items);
          }
          setHasMore(data.hasMore || false);
          setTotal(data.total || 0);
          setShowBackdrop(false);
          setLoading(false);
        }
      })
      .catch(error => {
        console.error('Error fetching issues:', error);
        if (shouldRedirectToLogin(error)) {
          setRedirect({ trigger: true, route: '/login' })
        }
      });
  }

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const currentOffset = offset;
    const endpointTab = tabValue === 'pending' ? 'active' : tabValue;
    const qParam = searchRef.current ? `&q=${encodeURIComponent(searchRef.current)}` : '';
    apiClient.get(`issues/${endpointTab}/list?limit=${PAGE_SIZE}&offset=${currentOffset}${qParam}`, {}, { action: 'fetch_issues_more' })
      .then(response => {
        if (response.status === 200) {
          const data = response.data.data || {};
          const newItems = data.items || [];
          if (tabValue === 'pending') {
            setActiveIssues(prev => [...prev, ...newItems]);
          } else if (tabValue === 'active') {
            setActiveIssues(prev => [...prev, ...newItems]);
          } else if (tabValue === 'resolved') {
            setResolvedIssues(prev => [...prev, ...newItems]);
          }
          setOffset(currentOffset + PAGE_SIZE);
          setHasMore(data.hasMore || false);
        }
        setLoadingMore(false);
      })
      .catch(error => {
        console.error('Error loading more issues:', error);
        setLoadingMore(false);
      });
  };

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        loadMore();
      }
    }, { rootMargin: '100px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, tabValue, offset]);

  React.useEffect(() => {
    setNavbarState({...navbarState, showBack: true, showOptions: false, showSearch: false})
    if (tab !== null) { 
      setRedirect({ trigger: true, route: '/inicio' });
      setTabValue(tab);
    }
    if (tabValue === 'pending') {
      if (activeIssues.length === 0) {
        fetchInfo('active');
      }
    } else {
      fetchInfo(tabValue);
    }
  }, [tabValue]);

  const debounceSkipRef = React.useRef(true);
  React.useEffect(() => {
    if (debounceSkipRef.current) {
      debounceSkipRef.current = false;
      return;
    }
    if (tabValue === 'pending') {
      fetchInfo('active');
    } else {
      fetchInfo(tabValue);
    }
  }, [debouncedSearch]);

  const [swipeDirection, setSwipeDirection] = React.useState(null);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (tabValue === 'active') {
        setSwipeDirection('left');
        setTabValue('resolved');
      }
    },
    onSwipedRight: () => {
      if (tabValue === 'resolved') {
        setSwipeDirection('right');
        setTabValue('active');
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  const pageVariants = {
    enter: (direction) => {
      return {
        x: direction === 'left' ? 1000 : -1000,
        opacity: 0
      };
    },
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => {
      return {
        x: direction === 'left' ? -1000 : 1000,
        opacity: 0
      };
    }
  };

  const pageTransition = {
    type: 'tween',
    ease: 'anticipate',
    duration: 0.5
  };

  const renderEmptyState = (message, description) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 200px)' }}>
      <Box sx={{ textAlign: 'center', px: 3 }}>
        <BuildCircleOutlinedIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{message}</Typography>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
      </Box>
    </Box>
  );

  const renderIssueList = (issues) => (
    issues.map((issue) => (
      <IssueListItem
        key={issue.id}
        issue={issue}
        apiUrl={apiUrl}
        stateFriendlyNames={stateFriendlyNames}
        showPendingDot={issue.estado.estado === 'reportado' && AuthService.getCurrentUserRole() === 'admin'}
        onClick={() => setRedirect({ trigger: true, route: `/reporte?id=${issue.id}` })}
      />
    ))
  );

  return (
    <Container maxWidth="sm" {...swipeHandlers}>
      {redirect.trigger && (
        <Navigate to={redirect.route} replace={true} />
      )}
    <Box sx={{ mt: 0 }}>
      <Snackbar open={snackbar.open} autoHideDuration={8000} anchorOrigin={{ vertical: "top", horizontal: "center" }} sx={{mt: "3.2rem"}} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Backdrop open={showBackdrop} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.default', pt: 1.5, pb: 0.5 }}>
        <Box sx={{ mb: 2 }}>
          <PageHeader
            title="Reportes"
            icon="shield-warning"
            onBack={() => navigate(-1)}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: showSearch ? 1 : 0.5 }}>
          <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5, flexGrow: 1 }}>
            <Chip
              label="Activos"
              clickable
              color={tabValue === 'active' ? 'primary' : 'default'}
              variant={tabValue === 'active' ? 'filled' : 'outlined'}
              onClick={(e) => handleTabChange(e, 'active')}
              sx={{ fontWeight: 700 }}
            />
            {AuthService.getCurrentUserRole() === 'admin' && (
              <Badge badgeContent={pendingCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', height: 18, minWidth: 18 } }}>
                <Chip
                  label="Pendientes"
                  clickable
                  color={tabValue === 'pending' ? 'primary' : 'default'}
                  variant={tabValue === 'pending' ? 'filled' : 'outlined'}
                  onClick={(e) => handleTabChange(e, 'pending')}
                  sx={{ fontWeight: 700 }}
                />
              </Badge>
            )}
            <Chip
              label="Resueltos"
              clickable
              color={tabValue === 'resolved' ? 'primary' : 'default'}
              variant={tabValue === 'resolved' ? 'filled' : 'outlined'}
              onClick={(e) => handleTabChange(e, 'resolved')}
              sx={{ fontWeight: 700 }}
            />
          </Stack>
          <IconButton
            onClick={handleToggleSearch}
            sx={{ width: 40, height: 40, borderRadius: '10px', border: '1px solid', borderColor: 'divider', color: 'text.primary' }}
          >
            <SearchIcon />
          </IconButton>
        </Box>
        {showSearch && (
          <TextField
            fullWidth
            size="small"
            id="search"
            sx={{ textAlign: 'center', mb: 0, mt: 0.5 }}
            label="Buscar"
            variant="outlined"
            value={searchValue}
            onChange={handleSearchChange}
            inputRef={searchInputRef}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleToggleSearch}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
              style: { borderRadius: '8px' }
            }}
          />
        )}
      </Box>

      <AnimatePresence initial={false} custom={swipeDirection}>
        <motion.div
          key={tabValue}
          custom={swipeDirection}
          variants={pageVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={pageTransition}
        >
          {tabValue === "resolved" ? (
            <>
              {!loading && (
                resolvedIssues.length === 0
                  ? (searchValue.trim()
                    ? renderEmptyState('Sin resultados', `No hay reportes que coincidan con «${searchValue.trim()}».`)
                    : renderEmptyState("No hay ningún reporte resuelto", "Aquí podrás ver los reportes que estén resueltos."))
                  : renderIssueList(resolvedIssues)
              )}
            </>
          ) : tabValue === "pending" ? (
            <>
              {!loading && (
                activeIssues.filter((issue) => issue.estado?.estado === 'reportado').length === 0
                  ? renderEmptyState("No hay ningún reporte pendiente", "Aquí podrás ver los reportes que estén pendientes de revisar.")
                  : renderIssueList(activeIssues.filter((issue) => issue.estado?.estado === 'reportado'))
              )}
            </>
          ) : (
            <>
              {!loading && (
                activeIssues.length === 0
                  ? (searchValue.trim()
                    ? renderEmptyState('Sin resultados', `No hay reportes que coincidan con «${searchValue.trim()}».`)
                    : renderEmptyState("No hay ningún reporte activo", "Aquí podrás ver los reportes que estén activos."))
                    : renderIssueList(activeIssues)
              )}
            </>
          )}
        </motion.div>
      </AnimatePresence>
      {hasMore && (
        <div ref={sentinelRef} style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {loadingMore && <CircularProgress size={24} />}
        </div>
      )}
      {!hasMore && (activeIssues.length > 0 || resolvedIssues.length > 0) && tabValue !== 'pending' && (
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.75rem', py: 2 }}>
          No hay más registros
        </Typography>
      )}
    </Box>

    <Box
      sx={{
        position: 'fixed',
        bottom: 'calc(84px + env(safe-area-inset-bottom))',
        left: 'calc(var(--app-drawer-width, 0px) + (100vw - var(--app-drawer-width, 0px)) / 2)',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: 600,
        px: 2,
        zIndex: 1300,
        pointerEvents: 'none'
      }}
    >
      <Button
        fullWidth
        variant="contained"
        color="success"
        aria-label="reportar"
        onClick={() => setRedirect({ trigger: true, route: '/nuevoreporte' })}
        sx={{
          height: 56,
          borderRadius: '10px',
          fontWeight: 800,
          pointerEvents: 'auto'
        }}
      >
        Reportar
      </Button>
    </Box>
    </Container>
  );
}
